import { SolButton } from "@solstice/sol-react";
import { BrokerProps, ProfileProps } from "types";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LoadingPage } from "ui-molecules";
import { capitalizeWords, parseOriginalJSON } from "utils";
import ContentEditor from "./ContentEditor";

interface BioProps {
  profile: ProfileProps | null;
  setProfile?: any;
  isLoading?: boolean;
  broker?: BrokerProps | null;
  reloadBroker: () => void;
  profile_key: keyof ProfileProps;
  label?: string /** Label to display in case profile_key is not suitable (for example awards_html) */;
  initialValue?: string /** Initial value to populate the editor in case it cannot be obtained from the profile_key */;
}

const isModeValid = (mode?: string): mode is "general" | "manual" => {
  return mode === "general" || mode === "manual";
};

const ContentArea: React.FC<BioProps> = ({
  profile,
  setProfile,
  isLoading,
  broker,
  reloadBroker,
  profile_key,
  initialValue,
  label,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState<"general" | "manual">("general");

  useEffect(() => {
    if (searchParams?.get("mode")?.toLowerCase() !== mode) {
      const m = searchParams?.get("mode")?.toLowerCase();
      isModeValid(m) ? setMode(m) : setMode("general");
    }
  }, [searchParams, mode]);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!profile) {
    return null;
  }

  const profile_key_display = label ? label : capitalizeWords(profile_key, "_");

  return (
    <>
      <div className="w-full overflow-y-auto flex flex-col relative h-full px-10 pt-8">
        {mode === "general" && (
          <>
            <div className="mb-6">
              <h2 className="text-xl mb-1">{profile_key_display}</h2>
            </div>

            <div className="flex flex-row items-center space-x-4 mb-8">
              <SolButton
                variant="secondary"
                onSol-click={() => {
                  searchParams.set("mode", "manual");
                  setSearchParams(searchParams);
                }}
              >
                Update {profile_key_display}
              </SolButton>
            </div>

            <div
              className="bio-preview mb-12"
              dangerouslySetInnerHTML={{
                __html: parseOriginalJSON(
                  initialValue ? initialValue : profile[profile_key] || ""
                ),
              }}
            />
          </>
        )}

        {mode === "manual" ? (
          <ContentEditor
            profile={profile}
            setProfile={setProfile}
            broker={broker}
            reloadBroker={reloadBroker}
            profile_key={profile_key}
            initialValue={initialValue}
          />
        ) : null}
      </div>
    </>
  );
};

export default ContentArea;
